import { useMemo } from "react";
import type { CatalogPrice } from "@ferrivia/api-contract";
import { useSession } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import { Sheet, SheetContent, SheetTitle } from "@ferrivia/ui";
import { useAppContext } from "../../app/useAppContext";
import { useCatalog } from "../../hooks/useCatalog";
import { useDismissOnBack } from "../../hooks/useDismissOnBack";
import { IronPanelPurchase } from "./IronPanelPurchase";
import { useCheckout } from "./useCheckout";

/** The iron panel purchase, opened from the Iron infusion tab's labs step. */
export function IronPanelPurchaseSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("client");
  const { rootElement } = useAppContext();

  useDismissOnBack(open, () => onOpenChange(false), "ironPanelPurchase");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent container={rootElement} aria-describedby={undefined}>
        <SheetTitle>{t("store.products.labPanel.name", "Laboratoriopaketti")}</SheetTitle>
        <PurchaseBody onChatOpened={() => onOpenChange(false)} />
      </SheetContent>
    </Sheet>
  );
}

// Inside SheetContent, so the catalog is only fetched once the sheet opens.
function PurchaseBody({ onChatOpened }: { onChatOpened: () => void }) {
  const { trust } = useSession();
  const { state } = useCatalog();

  // slug -> its first active price. Every product sold here carries exactly
  // one active price.
  const priceBySlug = useMemo(() => {
    const map = new Map<string, CatalogPrice>();
    if (state.status === "success") {
      for (const product of state.data.products) {
        const price = product.prices[0];
        if (price) map.set(product.slug, price);
      }
    }
    return map;
  }, [state]);

  const handleOrder = useCheckout(priceBySlug);

  // Not the "unavailable" line while the catalog is still in flight.
  if (state.status === "loading") return null;

  return (
    <IronPanelPurchase
      priceBySlug={priceBySlug}
      disabled={trust !== "verified"}
      onOrder={handleOrder}
      onChatOpened={onChatOpened}
    />
  );
}
